"use client";

import { CookieManager } from "react-cookie-manager";

/**
 * CookieConsentProvider wraps the application with GDPR-compliant cookie consent management.
 *
 * Features:
 * - Shows banner for all users (geolocation disabled - cookiekit.io domain expired)
 * - Manages consent for analytics cookies (PostHog)
 * - Allows users to manage consent preferences
 *
 * Cookie Categories:
 * - Strictly Necessary: Authentication & payment processing (always enabled)
 *   - Stripe payment cookies are strictly necessary per GDPR Article 6(1)(b)
 *     when users initiate payment transactions
 * - Analytics: PostHog tracking cookies (requires consent)
 *
 * Note: Automatic blocking is disabled because:
 * 1. Stripe's fraud prevention iframe (stripe.network) was incorrectly blocked
 * 2. Payment processing cookies are "strictly necessary" under GDPR
 * 3. PostHog consent is manually managed via PostHogProvider
 */
export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  return (
    <CookieManager
      displayType="banner"
      position="bottom"
      theme="dark"
      // Geolocation disabled - cookiekit.io domain expired, show banner for all users
      disableGeolocation={true}
      // Disable automatic blocking to prevent false positive on Stripe payment iframes
      // (react-cookie-manager incorrectly blocks stripe.network as advertising domain)
      disableAutomaticBlocking={true}
      // Privacy policy URL
      privacyPolicyUrl="/privacy"
      cookieCategories={{ Analytics: true, Social: false, Advertising: false }}
      // Customize button text
      // Callbacks for tracking consent decisions
      onAccept={() => {
        console.log("Cookie consent accepted");
      }}
      onDecline={() => {
        console.log("Cookie consent declined");
      }}
      onManage={(preferences) => {
        console.log("Cookie consent managed:", preferences);
      }}
      // Custom translations for cookie categories and descriptions
      translations={{
        // Banner customization
        title: "We Value Your Privacy",
        message:
          "We use cookies to enhance your experience, analyze site usage, and process payments. " +
          "You can choose which cookies you accept below.",

        // Essential/Necessary cookies (always enabled)
        manageEssentialTitle: "Strictly Necessary",
        manageEssentialSubtitle:
          "Essential cookies required for authentication, payment processing, security, and core functionality. " +
          "Includes Stripe payment cookies for fraud prevention and secure transactions. " +
          "These cookies cannot be disabled as they are necessary for the website to function.",

        // Analytics cookies (PostHog)
        manageAnalyticsTitle: "Analytics",
        manageAnalyticsSubtitle:
          "Help us understand how you use our site so we can improve your experience. " +
          "We use PostHog analytics to track page views and user interactions.",

        // Privacy policy link text
        privacyPolicyText: "Privacy Policy",
      }}
    >
      {children}
    </CookieManager>
  );
}
